import { BetaBadge } from "@/components/ui/beta-badge";
import {
  Alert,
  blue,
  Checkmark2Icon,
  FaroChip,
  FaroRadio,
  FaroRadioGroup,
  FaroText,
  grey,
  red,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { assert, validateEnumValue } from "@faro-lotv/foundation";
import { FloorplanGenerationMeasurementUnit } from "@faro-lotv/service-wires";
import { Divider, Link } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { PropsWithChildren, useState, type JSX } from "react";
import generateFloorPlanPromoImageUrl from "./generate-floor-plan-promo-image.png";

type FloorPlanOptionsDialogProps = TokenInfoProps & {
  /** The initial state of the measurement unit. */
  initialMeasurementUnit: FloorplanGenerationMeasurementUnit;

  /** Callback when the user selects a different measurement unit. */
  onMeasurementUnitChanged(newUnit: FloorplanGenerationMeasurementUnit): void;
};

/** @returns Dialog contents for when the user has sufficient token funds for a floor plan generation. */
export function FloorPlanOptionsDialog({
  availableTokens,
  requiredTokens,
  initialMeasurementUnit,
  onMeasurementUnitChanged,
}: FloorPlanOptionsDialogProps): JSX.Element {
  const [measurementUnit, setMeasurementUnit] = useState(
    initialMeasurementUnit,
  );

  return (
    <Stack sx={{ gap: 2 }}>
      <FaroText variant="bodyM">
        Use your available tokens to generate a floor plan. The floor plan
        generation will take 1-3 business days.
      </FaroText>

      <Box component="div">
        <FaroRadioGroup
          label="Units"
          value={measurementUnit}
          onChange={(ev) => {
            const newUnit = ev.target.value;
            assert(
              validateEnumValue(newUnit, FloorplanGenerationMeasurementUnit),
              "invalid measurement unit",
            );
            setMeasurementUnit(newUnit);
            onMeasurementUnitChanged(newUnit);
          }}
        >
          <FaroRadio
            value={FloorplanGenerationMeasurementUnit.metric}
            label="Metric"
          />
          <FaroRadio
            value={FloorplanGenerationMeasurementUnit.imperial}
            label="Imperial"
          />
        </FaroRadioGroup>
      </Box>

      <ActionCostInfo
        availableTokens={availableTokens}
        requiredTokens={requiredTokens}
      />
    </Stack>
  );
}

type RequestMoreTokensDialogProps = TokenInfoProps & {
  /** Whether the current user is allowed to buy tokens */
  isAllowedToBuyTokens: boolean;

  /** The URL to the subscriptions page where more tokens can be bought */
  subscriptionsUrl: string;
};

/** @returns Dialog contents for when the user has insufficient token funds for a floor plan generation */
export function RequestMoreTokensDialog({
  availableTokens,
  requiredTokens,
  isAllowedToBuyTokens,
  subscriptionsUrl,
}: RequestMoreTokensDialogProps): JSX.Element {
  return (
    <Stack sx={{ gap: 2 }}>
      <ActionCostInfo
        availableTokens={availableTokens}
        requiredTokens={requiredTokens}
      />

      {isAllowedToBuyTokens ? (
        <FaroText variant="bodyM">
          Manage your tokens in the{" "}
          <Link
            component="a"
            href={subscriptionsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            subscription
          </Link>{" "}
          page.
        </FaroText>
      ) : (
        <FaroText variant="bodyM">
          To request more tokens you need to contact your workspace admin.
        </FaroText>
      )}
    </Stack>
  );
}

type FloorPlanOrderSummaryDialogProps = TokenInfoProps & {
  /** The tokens remaining after the action has been performed. */
  remainingTokens: number;
};

/** @returns Dialog contents to summarize the floor plan order. */
export function FloorPlanOrderSummaryDialog({
  remainingTokens,
  requiredTokens,
}: FloorPlanOrderSummaryDialogProps): JSX.Element {
  return (
    <Stack sx={{ gap: 4, alignItems: "stretch", textAlign: "left" }}>
      <FaroText variant="bodyM">
        Before submitting your order, please review and confirm the following
        details:
      </FaroText>

      <Stack direction="row" sx={{ width: "100%" }}>
        <FaroText variant="bodyM" sx={{ flexGrow: 1 }}>
          Token usage:{" "}
          <b>
            <TranslateVar name="tokens">{requiredTokens}</TranslateVar>
          </b>
        </FaroText>

        <FaroText variant="bodyM" sx={{ flexGrow: 1 }}>
          Token left:{" "}
          <b>
            <TranslateVar name="tokens">{remainingTokens}</TranslateVar>
          </b>
        </FaroText>
      </Stack>

      <Alert
        variant="warning"
        title={
          <FaroText variant="bodyM">
            Tokens used for this order <b>are not refundable.</b>
          </FaroText>
        }
      />

      <Alert
        variant="warning"
        title={
          <FaroText variant="bodyM">
            Once submitted, this order <b>cannot be canceled.</b>
          </FaroText>
        }
      />
    </Stack>
  );
}

type FloorPlanGenerationStartedDialogProps = {
  /** The tokens remaining after the action has been performed. */
  remainingTokens: number;
};

/** @returns Dialog contents for when the generation has been started successfully. */
export function FloorPlanGenerationStartedDialog({
  remainingTokens,
}: FloorPlanGenerationStartedDialogProps): JSX.Element {
  return (
    <Stack sx={{ gap: 4, alignItems: "center", textAlign: "center" }}>
      <FaroText variant="heading24">
        Thank you,
        <br /> your request has been sent.
      </FaroText>

      <BigCheckmark />

      <FaroText variant="bodyM">
        The floor plan will be ready within 1-3 business days.
        <br /> You'll receive an email when it's available for download.
      </FaroText>

      <TokenInfoBox availableTokens={remainingTokens} requiredTokens={0}>
        <FaroText variant="bodyM">Remaining tokens</FaroText>{" "}
      </TokenInfoBox>
    </Stack>
  );
}

/** @returns a big checkmark in a box to indicate success */
function BigCheckmark(): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        width: 84,
        height: 84,
        color: blue[500],
        background: grey[50],
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "10px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Checkmark2Icon sx={{ width: 64, height: 64 }} />
    </Box>
  );
}

/** @returns A small info box informing the user about the cost of an action. */
function ActionCostInfo({
  availableTokens,
  requiredTokens,
}: TokenInfoProps): JSX.Element {
  return (
    <TokenInfoBox
      availableTokens={availableTokens}
      requiredTokens={requiredTokens}
    >
      <FaroText variant="bodyM">
        This action requires{" "}
        <b>
          <TranslateVar name="requiredTokens">{requiredTokens}</TranslateVar>{" "}
          Tokens.
        </b>
      </FaroText>
    </TokenInfoBox>
  );
}

type TokenInfoProps = {
  /** The token balance available to the user */
  availableTokens: number;

  /** The token balance required to perform the action */
  requiredTokens: number;
};

/** @returns a box to display text with the current available amount of tokens in a chip. */
function TokenInfoBox({
  availableTokens,
  requiredTokens,
  children,
}: PropsWithChildren<TokenInfoProps>): JSX.Element {
  const hasEnoughTokens = availableTokens >= requiredTokens;

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
        background: grey[50],
        border: `1px solid ${grey[200]}`,
        borderRadius: 1,
        px: 0.75,
        py: 1.5,
        width: "100%",
      }}
    >
      {children}

      <FaroChip
        variant="outlined"
        label={
          <>
            <TranslateVar name="availableTokens">
              {availableTokens}
            </TranslateVar>{" "}
            tokens available
          </>
        }
        color={hasEnoughTokens ? blue[500] : red[500]}
        backgroundColor={hasEnoughTokens ? blue[50] : red[50]}
        borderColor={hasEnoughTokens ? blue[100] : red[100]}
      />
    </Stack>
  );
}

const FLOOR_PLAN_GENERATION_INDUSTRIES = [
  "Architecture and Interior Design",
  "Real Estate",
  "Facility Management",
  "Engineering and Construction",
  "Retail and Commercial Design",
  "Education and Healthcare",
  "Public safety",
  "Insurance",
  "Other",
];

type FloorPlanGenerationBetaMarketingFeedbackProps = {
  /** Callback executed when the user selects an industry. */
  onIndustryChanged(industryName: string): void;
};

/** @returns the dialog meant to gather from beta users, what they would like to use the floor plans for */
export function FloorPlanGenerationBetaMarketingFeedback({
  onIndustryChanged,
}: FloorPlanGenerationBetaMarketingFeedbackProps): JSX.Element {
  return (
    <Stack direction="row" sx={{ gap: 2 }}>
      <Box
        component="img"
        src={generateFloorPlanPromoImageUrl}
        sx={{ width: "50%", objectFit: "contain", background: grey[100] }}
      />
      <Stack sx={{ gap: 2 }}>
        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
          <FaroText variant="heading20">
            Floor Plan Generation from Point Clouds
          </FaroText>
          <BetaBadge />
        </Stack>
        <Divider />
        <Stack sx={{ gap: 1 }}>
          <FaroText variant="bodyM">
            We're excited to let you try out our new beta feature designed to
            help you turn complex 3D scans into 2D floor plans.
          </FaroText>
          <FaroText variant="bodyM">
            To help us tailor the experience, could you tell us which industry
            you work in?
          </FaroText>
        </Stack>

        {/** Using an additional box here to prevent flex box gap from parent to be applied inside the radio group */}
        <Box component="div">
          <FaroRadioGroup label="What option best describes your industry?">
            {FLOOR_PLAN_GENERATION_INDUSTRIES.map((industry) => (
              <FaroRadio
                key={industry}
                label={industry}
                value={industry}
                onChange={() => onIndustryChanged(industry)}
              />
            ))}
          </FaroRadioGroup>
        </Box>
      </Stack>
    </Stack>
  );
}
